import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation } from '@tanstack/react-query';
import PageHeader from '@/components/ui/PageHeader';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { motion } from 'framer-motion';
import { User, MapPin, Church, Camera, CheckCircle, Upload } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { createPageUrl } from "@/utils";
import { optimizeProfileImage } from '@/lib/imageCompression';
import { notifyAdminsAboutIntake } from '@/lib/adminIntakeNotifications';
import ProfilePhotoCropper from '@/components/profile/ProfilePhotoCropper';

export default function CadastroMembro() {
  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedPhotoFile, setSelectedPhotoFile] = useState(null);
  const [photoInputKey, setPhotoInputKey] = useState(0);
  const [formData, setFormData] = useState({
    full_name: '',
    cpf: '',
    birth_date: '',
    age: '',
    phone: '',
    email: '',
    gender: '',
    marital_status: '',
    parent_name: '',
    parent_phone: '',
    cep: '',
    address: '',
    address_number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: '',
    congregation: '',
    membership_date: '',
    baptism_date: '',
    ministries: [],
    group_id: '',
    photo_url: '',
    observations: ''
  });

  const { data: groups = [] } = useQuery({
    queryKey: ['groups'],
    queryFn: () => base44.entities.Group.filter({ is_active: true })
  });

  const { data: currentUser } = useQuery({
    queryKey: ['currentUser'],
    queryFn: () => base44.auth.me(),
    retry: false
  });

  const { data: ministries = [] } = useQuery({
    queryKey: ['ministries'],
    queryFn: () => base44.entities.Ministry.filter({ is_active: true })
  });

  const createMutation = useMutation({
    mutationFn: async (data) => {
      const member = await base44.entities.Member.create(data);
      await notifyAdminsAboutIntake({ type: 'member_signup_request', recordId: member.id }).catch((error) => {
        console.error('Erro ao notificar administradores sobre cadastro:', error);
      });
      return member;
    },
    onSuccess: () => setSubmitted(true),
    onError: (error) => toast.error(error?.message || 'Erro ao enviar cadastro. Tente novamente.')
  });

  // Calculate age from birth date
  useEffect(() => {
    if (formData.birth_date) {
      const today = new Date();
      const birth = parseDateBR(formData.birth_date);
      if (!birth) {
        setFormData(prev => ({ ...prev, age: '' }));
        return;
      }
      let age = today.getFullYear() - birth.getFullYear();
      const m = today.getMonth() - birth.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
        age--;
      }
      setFormData(prev => ({ ...prev, age }));
      return;
    }
    setFormData(prev => ({ ...prev, age: '' }));
  }, [formData.birth_date]);

  useEffect(() => {
    if (!currentUser?.email) return;
    setFormData((prev) => {
      if (String(prev.email || '').trim()) return prev;
      return { ...prev, email: String(currentUser.email).trim().toLowerCase() };
    });
  }, [currentUser?.email]);

  // Fetch address from CEP
  const fetchAddress = async (cep) => {
    const cleanCep = cep.replace(/\D/g, '');
    if (cleanCep.length === 8) {
      try {
        const response = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
        const data = await response.json();
        if (!data.erro) {
          setFormData(prev => ({
            ...prev,
            address: data.logradouro,
            neighborhood: data.bairro,
            city: data.localidade,
            state: data.uf
          }));
        }
      } catch (error) {
        console.log('Erro ao buscar CEP');
      }
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setSelectedPhotoFile(file);
    setPhotoInputKey((current) => current + 1);
  };

  const handleCroppedPhotoUpload = async (croppedFile) => {
    setUploading(true);
    try {
      const optimizedFile = await optimizeProfileImage(croppedFile);
      const { file_url } = await base44.integrations.Core.UploadFile({ file: optimizedFile });
      setFormData(prev => ({ ...prev, photo_url: file_url }));
      setSelectedPhotoFile(null);
      toast.success('Foto enviada com sucesso!');
    } catch (error) {
      toast.error(error?.message || 'Erro ao enviar foto.');
    } finally {
      setUploading(false);
    }
  };

  const formatCPF = (value) => {
    return value
      .replace(/\D/g, '')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})/, '$1-$2')
      .replace(/(-\d{2})\d+?$/, '$1');
  };

  const formatPhone = (value) => {
    return value
      .replace(/\D/g, '')
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{5})(\d)/, '$1-$2')
      .replace(/(-\d{4})\d+?$/, '$1');
  };

  const formatCEP = (value) => {
    return value
      .replace(/\D/g, '')
      .replace(/(\d{5})(\d)/, '$1-$2')
      .replace(/(-\d{3})\d+?$/, '$1');
  };

  const splitDateBR = (value) => {
    const [day = '', month = '', year = ''] = String(value || '').split('/');
    return { day, month, year };
  };

  const buildDateBR = (day, month, year) => {
    if (!day && !month && !year) return '';
    if (year) return `${day}/${month}/${year}`;
    if (month) return `${day}/${month}`;
    return day;
  };

  const updateDatePart = (field, part, rawValue) => {
    const digitsOnly = String(rawValue || '').replace(/\D/g, '');
    const maxByPart = { day: 2, month: 2, year: 4 };
    const nextPartValue = digitsOnly.slice(0, maxByPart[part] || 4);

    setFormData((prev) => {
      const current = splitDateBR(prev[field]);
      const next = { ...current, [part]: nextPartValue };
      return { ...prev, [field]: buildDateBR(next.day, next.month, next.year) };
    });
  };

  const parseDateBR = (value) => {
    const match = String(value || '').trim().match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (!match) return null;
    const [, dayStr, monthStr, yearStr] = match;
    const day = Number(dayStr);
    const month = Number(monthStr);
    const year = Number(yearStr);
    if (year < 1900 || year > 2100) return null;
    const parsed = new Date(year, month - 1, day);
    if (
      parsed.getFullYear() !== year ||
      parsed.getMonth() !== month - 1 ||
      parsed.getDate() !== day
    ) {
      return null;
    }
    return parsed;
  };

  const toISODate = (value) => {
    const parsed = parseDateBR(value);
    if (!parsed) return undefined;
    const year = parsed.getFullYear();
    const month = String(parsed.getMonth() + 1).padStart(2, '0');
    const day = String(parsed.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleSubmit = async () => {
    if (!formData.full_name || !formData.phone || !formData.email) {
      toast.error('Preencha os campos obrigatórios: Nome, Telefone e Email.');
      return;
    }

    const normalizedEmail = String(formData.email || '').trim().toLowerCase();
    const birthDateISO = toISODate(formData.birth_date);
    const membershipDateISO = toISODate(formData.membership_date);
    const baptismDateISO = toISODate(formData.baptism_date);

    if (formData.birth_date && !birthDateISO) {
      toast.error('Data de nascimento inválida. Use o formato DD/MM/AAAA.');
      return;
    }
    if (formData.membership_date && !membershipDateISO) {
      toast.error('Data de membresia inválida. Use o formato DD/MM/AAAA.');
      return;
    }
    if (formData.baptism_date && !baptismDateISO) {
      toast.error('Data de batismo inválida. Use o formato DD/MM/AAAA.');
      return;
    }

    const dataToSubmit = {
      full_name: formData.full_name,
      cpf: formData.cpf || undefined,
      birth_date: birthDateISO,
      age: formData.age || undefined,
      phone: formData.phone,
      email: normalizedEmail,
      gender: formData.gender || undefined,
      marital_status: formData.marital_status || undefined,
      parent_name: formData.parent_name || undefined,
      parent_phone: formData.parent_phone || undefined,
      cep: formData.cep || undefined,
      address: formData.address || undefined,
      address_number: formData.address_number || undefined,
      complement: formData.complement || undefined,
      neighborhood: formData.neighborhood || undefined,
      city: formData.city || undefined,
      state: formData.state || undefined,
      congregation: formData.congregation || undefined,
      membership_date: membershipDateISO,
      baptism_date: baptismDateISO,
      ministries: formData.ministries.length > 0 ? formData.ministries : undefined,
      group_id: formData.group_id || undefined,
      photo_url: formData.photo_url || undefined,
      observations: formData.observations || undefined,
      user_id: currentUser?.id || undefined,
      status: 'Pendente'
    };

    createMutation.mutate(dataToSubmit);
  };

  const isMinor = formData.age && formData.age < 18;

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-gradient-to-br from-indigo-600 to-purple-700 text-white pt-8 pb-20 px-4">
          <div className="max-w-lg mx-auto">
            <PageHeader title="Cadastro de Membro" showBack={true} />
          </div>
        </div>

        <div className="max-w-lg mx-auto px-4 -mt-14">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-lg p-8 text-center"
          >
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Cadastro Concluído!</h2>
            <p className="text-gray-600 mb-6">
              Seu cadastro foi realizado com sucesso! Você já pode acessar todos os recursos do aplicativo.
            </p>
            <Button 
              onClick={() => window.location.href = createPageUrl('Home')}
              className="w-full"
            >
              Ir para o Início
            </Button>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <ProfilePhotoCropper
        file={selectedPhotoFile}
        open={!!selectedPhotoFile}
        isProcessing={uploading}
        onCancel={() => setSelectedPhotoFile(null)}
        onConfirm={handleCroppedPhotoUpload}
      />

      <div className="bg-gradient-to-br from-indigo-600 to-purple-700 text-white pt-8 pb-6 px-4">
        <div className="max-w-lg mx-auto">
          <PageHeader title="Cadastro de Membro" subtitle="Faça parte da nossa família" showBack={true} />
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6">
        {/* Progress Steps */}
        <div className="flex justify-between mb-6">
          {[1, 2, 3, 4].map((s) => (
            <div key={s} className="flex-1 flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step >= s ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-500'
              }`}>
                {s}
              </div>
              {s < 4 && (
                <div className={`flex-1 h-1 mx-2 ${step > s ? 'bg-indigo-600' : 'bg-gray-200'}`} />
              )}
            </div>
          ))}
        </div>

        {/* Step 1: Personal Data */}
        {step === 1 && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white rounded-2xl shadow-sm p-6"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
                <User className="w-5 h-5 text-indigo-600" />
              </div>
              <h2 className="font-semibold text-gray-900">Dados Pessoais</h2>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome Completo *</label>
                <Input
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  placeholder="Seu nome completo"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">CPF *</label>
                <Input
                  value={formData.cpf}
                  onChange={(e) => setFormData({ ...formData, cpf: formatCPF(e.target.value) })}
                  placeholder="000.000.000-00"
                  maxLength={14}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data de Nascimento</label>
                  <div className="grid grid-cols-3 gap-2">
                    <Input
                      type="text"
                      inputMode="numeric"
                      placeholder="DD"
                      maxLength={2}
                      value={splitDateBR(formData.birth_date).day}
                      onChange={(e) => updateDatePart('birth_date', 'day', e.target.value)}
                    />
                    <Input
                      type="text"
                      inputMode="numeric"
                      placeholder="MM"
                      maxLength={2}
                      value={splitDateBR(formData.birth_date).month}
                      onChange={(e) => updateDatePart('birth_date', 'month', e.target.value)}
                    />
                    <Input
                      type="text"
                      inputMode="numeric"
                      placeholder="AAAA"
                      maxLength={4}
                      value={splitDateBR(formData.birth_date).year}
                      onChange={(e) => updateDatePart('birth_date', 'year', e.target.value)}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Idade</label>
                  <Input
                    type="number"
                    value={formData.age}
                    readOnly
                    placeholder="Calculada automaticamente"
                    className="bg-gray-50"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Telefone *</label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: formatPhone(e.target.value) })}
                  placeholder="(00) 00000-0000"
                  maxLength={15}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">E-mail *</label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="seu@email.com"
                />
              </div>

              <div>
               <label className="block text-sm font-medium text-gray-700 mb-1">Gênero</label>
               <select
                 value={formData.gender}
                 onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                 className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
               >
                 <option value="">Selecione</option>
                 <option value="Masculino">Masculino</option>
                 <option value="Feminino">Feminino</option>
               </select>
              </div>

              <div>
               <label className="block text-sm font-medium text-gray-700 mb-1">Estado Civil</label>
               <select
                 value={formData.marital_status}
                 onChange={(e) => setFormData({ ...formData, marital_status: e.target.value })}
                 className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
               >
                 <option value="">Selecione</option>
                 <option value="Solteiro(a)">Solteiro(a)</option>
                 <option value="Casado(a)">Casado(a)</option>
                 <option value="Divorciado(a)">Divorciado(a)</option>
                 <option value="Viúvo(a)">Viúvo(a)</option>
                 <option value="União Estável">União Estável</option>
               </select>
              </div>

              <div>
               <label className="block text-sm font-medium text-gray-700 mb-1">Congregação</label>
               <select
                 value={formData.congregation}
                 onChange={(e) => setFormData({ ...formData, congregation: e.target.value })}
                 className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
               >
                 <option value="">Selecione</option>
                 <option value="SEDE">SEDE</option>
                 <option value="PARQUE BRASÍLIA">PARQUE BRASÍLIA</option>
                 <option value="BAIRRO DE LOURDES">BAIRRO DE LOURDES</option>
               </select>
              </div>

              {isMinor && (
                <>
                  <div className="p-3 bg-amber-50 rounded-xl border border-amber-200 text-amber-800 text-sm">
                    <strong>Menor de idade:</strong> Preencha os dados do responsável.
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nome do Responsável *</label>
                    <Input
                      value={formData.parent_name}
                      onChange={(e) => setFormData({ ...formData, parent_name: e.target.value })}
                      placeholder="Nome completo"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Telefone do Responsável *</label>
                    <Input
                      value={formData.parent_phone}
                      onChange={(e) => setFormData({ ...formData, parent_phone: formatPhone(e.target.value) })}
                      placeholder="(00) 00000-0000"
                      maxLength={15}
                    />
                  </div>
                </>
              )}

              <Button className="w-full mt-4" onClick={() => setStep(2)}>
                Próximo
              </Button>
            </div>
          </motion.div>
        )}

        {/* Step 2: Address */}
        {step === 2 && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white rounded-2xl shadow-sm p-6"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                <MapPin className="w-5 h-5 text-emerald-600" />
              </div>
              <h2 className="font-semibold text-gray-900">Endereço</h2>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">CEP *</label>
                <Input
                  value={formData.cep}
                  onChange={(e) => {
                    const cep = formatCEP(e.target.value);
                    setFormData({ ...formData, cep });
                    fetchAddress(cep);
                  }}
                  placeholder="00000-000"
                  maxLength={9}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Endereço</label>
                <Input
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Rua, Avenida..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Número</label>
                  <Input
                    value={formData.address_number}
                    onChange={(e) => setFormData({ ...formData, address_number: e.target.value })}
                    placeholder="Nº"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Complemento</label>
                  <Input
                    value={formData.complement}
                    onChange={(e) => setFormData({ ...formData, complement: e.target.value })}
                    placeholder="Apto, Bloco..."
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bairro</label>
                <Input
                  value={formData.neighborhood}
                  onChange={(e) => setFormData({ ...formData, neighborhood: e.target.value })}
                  placeholder="Bairro"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cidade</label>
                  <Input
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder="Cidade"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
                  <Input
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    placeholder="UF"
                    maxLength={2}
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-4">
                <Button variant="outline" className="mbv-back-action flex-1" onClick={() => setStep(1)}>
                  Voltar
                </Button>
                <Button className="flex-1" onClick={() => setStep(3)}>
                  Próximo
                </Button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Step 3: Church Data */}
        {step === 3 && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white rounded-2xl shadow-sm p-6"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                <Church className="w-5 h-5 text-purple-600" />
              </div>
              <h2 className="font-semibold text-gray-900">Dados da Igreja</h2>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data de Membresia</label>
                  <div className="grid grid-cols-3 gap-2">
                    <Input
                      type="text"
                      inputMode="numeric"
                      placeholder="DD"
                      maxLength={2}
                      value={splitDateBR(formData.membership_date).day}
                      onChange={(e) => updateDatePart('membership_date', 'day', e.target.value)}
                    />
                    <Input
                      type="text"
                      inputMode="numeric"
                      placeholder="MM"
                      maxLength={2}
                      value={splitDateBR(formData.membership_date).month}
                      onChange={(e) => updateDatePart('membership_date', 'month', e.target.value)}
                    />
                    <Input
                      type="text"
                      inputMode="numeric"
                      placeholder="AAAA"
                      maxLength={4}
                      value={splitDateBR(formData.membership_date).year}
                      onChange={(e) => updateDatePart('membership_date', 'year', e.target.value)}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data de Batismo</label>
                  <div className="grid grid-cols-3 gap-2">
                    <Input
                      type="text"
                      inputMode="numeric"
                      placeholder="DD"
                      maxLength={2}
                      value={splitDateBR(formData.baptism_date).day}
                      onChange={(e) => updateDatePart('baptism_date', 'day', e.target.value)}
                    />
                    <Input
                      type="text"
                      inputMode="numeric"
                      placeholder="MM"
                      maxLength={2}
                      value={splitDateBR(formData.baptism_date).month}
                      onChange={(e) => updateDatePart('baptism_date', 'month', e.target.value)}
                    />
                    <Input
                      type="text"
                      inputMode="numeric"
                      placeholder="AAAA"
                      maxLength={4}
                      value={splitDateBR(formData.baptism_date).year}
                      onChange={(e) => updateDatePart('baptism_date', 'year', e.target.value)}
                    />
                  </div>
                </div>
              </div>



              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Observações</label>
                <Textarea
                  value={formData.observations}
                  onChange={(e) => setFormData({ ...formData, observations: e.target.value })}
                  placeholder="Algo que queira compartilhar..."
                  rows={3}
                />
              </div>

              <div className="flex gap-3 mt-4">
                <Button variant="outline" className="mbv-back-action flex-1" onClick={() => setStep(2)}>
                  Voltar
                </Button>
                <Button className="flex-1" onClick={() => setStep(4)}>
                  Próximo
                </Button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Step 4: Photo */}
        {step === 4 && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white rounded-2xl shadow-sm p-6"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-rose-100 rounded-xl flex items-center justify-center">
                <Camera className="w-5 h-5 text-rose-600" />
              </div>
              <h2 className="font-semibold text-gray-900">Foto de Perfil</h2>
            </div>

            <div className="flex flex-col items-center">
              {formData.photo_url ? (
                <div className="relative">
                  <img
                    src={formData.photo_url}
                    alt="Foto de perfil"
                    className="w-32 h-32 rounded-full object-cover"
                  />
                  <button
                    onClick={() => setFormData({ ...formData, photo_url: '' })}
                    className="absolute -top-2 -right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center"
                  >
                    ×
                  </button>
                </div>
              ) : (
                <label className="w-32 h-32 rounded-full bg-gray-100 flex flex-col items-center justify-center cursor-pointer hover:bg-gray-200 transition-colors">
                  <input
                    key={photoInputKey}
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoUpload}
                    className="hidden"
                    disabled={uploading}
                  />
                  {uploading ? (
                    <LoadingSpinner />
                  ) : (
                    <>
                      <Upload className="w-8 h-8 text-gray-400" />
                      <span className="text-xs text-gray-500 mt-2">Adicionar foto</span>
                    </>
                  )}
                </label>
              )}
              <p className="text-sm text-gray-500 mt-4 text-center">
                A foto é opcional, mas ajuda a te identificarmos melhor.
              </p>
            </div>

            <div className="flex gap-3 mt-6">
              <Button variant="outline" className="mbv-back-action flex-1" onClick={() => setStep(3)}>
                Voltar
              </Button>
              <Button 
                className="flex-1 bg-indigo-600 hover:bg-indigo-700" 
                onClick={handleSubmit}
                disabled={createMutation.isPending}
              >
                {createMutation.isPending ? 'Enviando...' : 'Finalizar Cadastro'}
              </Button>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
